import { pxToRem } from '@deltek/specification-client/core/Utilities/unitUtils'
import { makeStyles, Theme, Typography } from '@material-ui/core'
import { ClearOutlined } from '@material-ui/icons'
import clsx from 'clsx'
import React, { FC, MouseEventHandler, ReactNode, useCallback } from 'react'

import { NavPromptMessageProps } from '@deltek/specification-client/core/Forms/NavPrompt'
import NavPromptDialogContainer from '@deltek/specification-client/core/Forms/NavPromptDialogContainer'
import SortableItem from 'bpm/components/sortable/SortableItem'
import listingColors from 'bpm/pages/ProductsPage/ProductDetailsPage/listingColors'
import { PROMPT_DELETE_OPTION, TITLE_DELETE_OPTION } from '../constants'

const { premiumPrimary, defaultLight, buttonSecondary } = listingColors

const useStyles = makeStyles((theme: Theme) => ({
    clearOutlined: {
        position: 'absolute',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        right: theme.spacing(1),
        marginBottom: theme.spacing(1),
        height: pxToRem(14),
        width: pxToRem(14),
        border: `1px solid ${premiumPrimary}`,
        borderRadius: 100,
        '&:hover': {
            cursor: 'pointer',
        },
        '&.selected': {
            border: `1px solid ${defaultLight}`,
        },
    },
    clearIcon: {
        color: premiumPrimary,
        height: pxToRem(10),
        width: pxToRem(10),
        '&.selected': {
            color: defaultLight,
        },
    },
    premProperty: {
        display: 'flex',
    },
    dndOptionContainer: {
        display: 'inline-flex',
        alignItems: 'center',
        position: 'relative',
    },
}))

interface DndOptionProps {
    children: ReactNode
    id: string
    isSelected: boolean
    onCloseClick: (id: string) => void
}

const DndOption: FC<DndOptionProps> = ({
    children,
    id,
    onCloseClick,
    isSelected,
}) => {
    const classes = useStyles()

    const handleCloseClick: MouseEventHandler<HTMLElement> = useCallback(
        (e) => {
            e.stopPropagation()
            return NavPromptDialogContainer(
                JSON.stringify({
                    title: TITLE_DELETE_OPTION,
                    preferredMessage: PROMPT_DELETE_OPTION,
                    buttonColor: buttonSecondary,
                    confirmText: 'Yes',
                    cancelText: 'No',
                } as NavPromptMessageProps),
                (callbackState) => {
                    if (callbackState) {
                        onCloseClick(id)
                    }
                }
            )
        },
        [id, onCloseClick]
    )

    return (
        <SortableItem
            id={id}
            role="button"
            className={classes.dndOptionContainer}
        >
            {children}
            <Typography
                component="span"
                className={clsx(
                    classes.clearOutlined,
                    isSelected ? 'selected' : ''
                )}
                role="button"
                tabIndex={0}
                onClick={handleCloseClick}
                data-testid={`deletePremium-${id}`}
            >
                <ClearOutlined
                    className={clsx(
                        classes.clearIcon,
                        isSelected ? 'selected' : ''
                    )}
                />
            </Typography>
        </SortableItem>
    )
}

export default DndOption
